/**
 * Chat sessions — list / create / patch / messages / run-status / share lifecycle.
 *
 * Mirrors ``backend/sessions/router.py``. The frontend's chat surface owns
 * this Api block; the requirements + workbench surfaces also reach in to
 * create sessions and amend Tool loadouts via the same client.
 */
import apiClient from "@lib/axios";
import type {
  ChatMessage,
  ChatSessionSummary,
  MessagesPage,
} from "@models/chat";
import {
  ChatSessionSummarySchema,
  MessagesPageSchema,
} from "@services/schemas";
import { z } from "zod";

export const sessionsApi = {
  /**
   * List chat sessions visible in the given workspace scope.
   *
   * ``organisationId``:
   *   null / undefined → personal chats (the caller's own, no org).
   *   <uuid>           → all chats from every member of that organisation.
   *
   * ``projectFilter`` (server distinguishes the three cases by URL shape):
   *   undefined → param omitted: no project filter, returns every session
   *               in scope. Used by HistoryPage which shows everything.
   *   null      → ``?project_id=`` (empty value): unscoped sessions only,
   *               i.e. those with no project pinned. Used by ``/chat``.
   *   string    → ``?project_id=<name>``: only that project's sessions.
   *               Used by ``/projects/$id/chat``.
   */
  list: async (
    organisationId?: string | null,
    projectFilter?: string | null,
    /** Surface filter — `workbench` or `requirements`. Used by the
     *  Copilot rail to find the latest session for the (project,
     *  surface) pair so each surface keeps its own conversation. */
    surfaceFilter?: "workbench" | "requirements" | null,
  ): Promise<ChatSessionSummary[]> => {
    const params = new URLSearchParams();
    if (organisationId) params.set("organisation_id", organisationId);
    if (projectFilter !== undefined) {
      params.set("project_id", projectFilter ?? "");
    }
    if (surfaceFilter) params.set("surface", surfaceFilter);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const { data } = await apiClient.get(`/sessions${qs}`);
    return z.array(ChatSessionSummarySchema).parse(data);
  },
  create: async (data?: {
    title?: string;
    project_id?: string;
    version_id?: string | null;
    /** Scope the new session to an organisation so every member sees it. */
    organisation_id?: string | null;
    /** Pin the chat surface at creation time so the chat runner uses
     *  the right agent on turn 1. */
    surface?: "workbench" | "requirements" | null;
    /** Multi-select board scope: omit / [] for whole project, [bid] for
     *  single-board pin, [a, b] for subset. Mirrors the chat composer's
     *  scope chip; immutable for the life of the session. */
    board_ids?: string[];
  }): Promise<ChatSessionSummary> => {
    const { data: result } = await apiClient.post("/sessions", data ?? {});
    return ChatSessionSummarySchema.parse(result);
  },
  updateTitle: async (
    sessionId: string,
    title: string,
  ): Promise<ChatSessionSummary> => {
    const { data } = await apiClient.patch(
      `/sessions/${encodeURIComponent(sessionId)}/title`,
      { title },
    );
    return ChatSessionSummarySchema.parse(data);
  },
  // `updateTools` was retired in May 2026. The server endpoint
  // `PATCH /sessions/{id}/tools` now returns 410 Gone.
  updateVersion: async (
    sessionId: string,
    versionId: string,
  ): Promise<ChatSessionSummary> => {
    const { data } = await apiClient.post(
      `/sessions/${encodeURIComponent(sessionId)}/version`,
      { version_id: versionId },
    );
    return ChatSessionSummarySchema.parse(data);
  },
  delete: async (sessionId: string): Promise<void> => {
    await apiClient.delete(`/sessions/${encodeURIComponent(sessionId)}`);
  },
  /**
   * Fetch one page of messages for a session.
   *
   * ``opts.beforeTs`` is the cursor returned as ``oldest_timestamp`` on
   * the previous page — pass it to load older history. Omit on first open
   * to receive the most recent slice. ``opts.limit`` overrides the
   * server's default page size (500 events ≈ 50–100 messages).
   */
  getMessages: async (
    sessionId: string,
    opts?: { beforeTs?: number | null; limit?: number },
  ): Promise<MessagesPage> => {
    const params = new URLSearchParams();
    if (opts?.beforeTs != null) {
      params.set("before_ts", String(opts.beforeTs));
    }
    if (opts?.limit != null) {
      params.set("limit", String(opts.limit));
    }
    const qs = params.toString();
    const { data } = await apiClient.get(
      `/sessions/${encodeURIComponent(sessionId)}/messages${qs ? `?${qs}` : ""}`,
    );
    return MessagesPageSchema.parse(data);
  },

  getActivity: async (
    sessionId: string,
  ): Promise<{
    entries: Array<{
      index: number;
      type: "tool_call" | "tool_result" | "thinking";
      label: string;
      args?: Record<string, unknown>;
      /** UX-1: tool_result entries carry a one-line summary of the
       *  return payload. Optional — empty when nothing meaningful
       *  could be extracted. */
      summary?: string;
    }>;
    complete: boolean;
  }> => {
    const { data } = await apiClient.get<{
      entries: Array<{
        index: number;
        type: "tool_call" | "tool_result" | "thinking";
        label: string;
        args?: Record<string, unknown>;
        summary?: string;
      }>;
      complete: boolean;
    }>(`/sessions/${encodeURIComponent(sessionId)}/activity`);
    return data;
  },

  /**
   * Status of the latest chat turn on this session. Returns null when
   * no run has been recorded yet (a brand-new session). The frontend
   * uses this to decide what to do after an SSE drop:
   *   running + fresh heartbeat → reattach
   *   running + stale heartbeat → likely hung; surface a warning
   *   completed                  → hydrate the final message
   *   failed | killed            → show error_summary
   */
  getRunStatus: async (
    sessionId: string,
  ): Promise<{
    run_id: string;
    session_id: string;
    status: "running" | "completed" | "failed" | "killed";
    started_at: string | null;
    last_heartbeat: string | null;
    completed_at: string | null;
    error_summary: string | null;
    last_event_index: number | null;
    heartbeat_age_s: number | null;
  } | null> => {
    const { data } = await apiClient.get<{
      run_id: string;
      session_id: string;
      status: "running" | "completed" | "failed" | "killed";
      started_at: string | null;
      last_heartbeat: string | null;
      completed_at: string | null;
      error_summary: string | null;
      last_event_index: number | null;
      heartbeat_age_s: number | null;
    } | null>(`/sessions/${encodeURIComponent(sessionId)}/run-status`);
    return data;
  },

  // ── Public-share lifecycle ─────────────────────────────────────────────────
  // Owner-side. Mints / lists / revokes share tokens for a session.
  // The actual public read of a shared chat goes through ``sharesApi``
  // (no auth) — see ``features/share/services/api.ts``.
  listShares: async (
    sessionId: string,
  ): Promise<
    Array<{
      token: string;
      source_session_id: string;
      snapshot_event_count: number;
      snapshot_message_count: number;
      created_at: string | null;
      view_count: number;
      last_viewed_at: string | null;
    }>
  > => {
    const { data } = await apiClient.get<
      Array<{
        token: string;
        source_session_id: string;
        snapshot_event_count: number;
        snapshot_message_count: number;
        created_at: string | null;
        view_count: number;
        last_viewed_at: string | null;
      }>
    >(`/sessions/${encodeURIComponent(sessionId)}/share`);
    return data;
  },
  createShare: async (
    sessionId: string,
  ): Promise<{
    token: string;
    snapshot_message_count: number;
    created_at: string | null;
  }> => {
    const { data } = await apiClient.post<{
      token: string;
      snapshot_message_count: number;
      created_at: string | null;
    }>(`/sessions/${encodeURIComponent(sessionId)}/share`);
    return data;
  },
  revokeShare: async (sessionId: string, token: string): Promise<void> => {
    await apiClient.delete(
      `/sessions/${encodeURIComponent(sessionId)}/share/${encodeURIComponent(token)}`,
    );
  },
};

// Re-export ChatMessage for `sharesApi` consumers that pulled the type from
// here in the old layout. The canonical home stays ``@models/chat``.
export type { ChatMessage };
